import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const tooltipStyle = {
  borderRadius: '8px',
  border: 'none',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  fontSize: '12px',
  padding: '8px 12px',
};

const ClientNPS = ({ data }) => {
  const quarterData = {};
  data.forEach(item => {
    const quarter = item.quarter || 'Overall';
    if (!quarterData[quarter]) quarterData[quarter] = { scores: [] };
    const score = parseFloat(item.averageScore) || 0;
    if (score > 0) quarterData[quarter].scores.push(score);
    if (item.individualScores && Array.isArray(item.individualScores)) {
      item.individualScores.forEach(s => {
        const val = parseFloat(s);
        if (val > 0) quarterData[quarter].scores.push(val);
      });
    }
  });

  const chartData = Object.entries(quarterData)
    .map(([quarter, { scores }]) => {
      const avg = scores.length > 0
        ? parseFloat((scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1))
        : 0;
      return { quarter, score: avg, responses: scores.length };
    })
    .sort((a, b) => a.quarter.localeCompare(b.quarter));

  const allScores = Object.values(quarterData).flatMap(d => d.scores);
  const overallAvg = allScores.length > 0
    ? parseFloat((allScores.reduce((a, b) => a + b, 0) / allScores.length).toFixed(1))
    : null;

  const getNPSCategory = (score) => {
    if (score >= 9) return { label: 'Promoter', color: '#10b981', bg: '#f0fdf4' };
    if (score >= 7) return { label: 'Passive', color: '#f59e0b', bg: '#fffbeb' };
    return { label: 'Detractor', color: '#ef4444', bg: '#fef2f2' };
  };

  const getBarColor = (score) => {
    if (score >= 9) return '#10b981';
    if (score >= 7) return '#f59e0b';
    if (score > 0) return '#ef4444';
    return '#d1d5db';
  };

  return (
    <div className="dashboard-card p-6 slide-in-up">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-semibold text-gray-800">Satisfaction Score</h3>
        {overallAvg !== null ? (
          <span
            className="text-xs font-medium px-2.5 py-1 rounded-full"
            style={{
              background: getNPSCategory(overallAvg).bg,
              color: getNPSCategory(overallAvg).color,
            }}
          >
            {overallAvg}/10 &middot; {getNPSCategory(overallAvg).label}
          </span>
        ) : (
          <div className="bg-purple-100 text-purple-800 text-xs font-medium px-2.5 py-1 rounded-full">
            NPS Score
          </div>
        )}
      </div>
      <p className="text-sm text-gray-600 mb-4">How likely to recommend Satellite Talent — by quarter</p>

      {chartData.length === 0 ? (
        <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
          <div className="text-center">
            <svg className="w-8 h-8 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p>No satisfaction data available yet</p>
          </div>
        </div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="quarter" tickLine={false} axisLine={false} style={{ fontSize: '12px' }} />
              <YAxis domain={[0, 10]} tickLine={false} axisLine={false} style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(value, _name, props) => [
                  `${value}/10 (${props.payload.responses} responses)`,
                  'Satisfaction Score'
                ]}
              />
              <Bar dataKey="score" radius={[4, 4, 0, 0]} barSize={30}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getBarColor(entry.score)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <div className="flex items-center justify-center space-x-4 mt-3 text-xs text-gray-500">
            <span className="flex items-center">
              <span className="w-2 h-2 rounded-full bg-emerald-500 mr-1"></span>Promoter (9-10)
            </span>
            <span className="flex items-center">
              <span className="w-2 h-2 rounded-full bg-amber-500 mr-1"></span>Passive (7-8)
            </span>
            <span className="flex items-center">
              <span className="w-2 h-2 rounded-full bg-red-500 mr-1"></span>Detractor (0-6)
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default ClientNPS;
